import React, { useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Chip, CircularProgress, Alert, ToggleButton, ToggleButtonGroup, TextField,
  MenuItem, Pagination, Stack, Tooltip, Dialog, DialogTitle, DialogContent, IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { da } from 'date-fns/locale';
import { adminApi } from '../../api/client';

type Level = 'info' | 'warn' | 'error';

interface EndpointRow {
  endpoint: string;
  errors: number | null;
  warnings: number | null;
  ok: number | null;
  errors24h: number | null;
  lastErrorAt: string | null;
  lastOkAt: string | null;
  avgDurationMs: number | null;
  maxItems: number | null;
}

interface ApiEvent {
  id: string;
  level: Level;
  endpoint: string;
  region: string | null;
  position: number | null;
  httpStatus: number | null;
  itemsFetched: number | null;
  durationMs: number | null;
  message: string | null;
  params: Record<string, unknown> | null;
  runId: string | null;
  createdAt: string;
}

interface SyncRun {
  id: string;
  kind: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  summary: any;
  errorCount: number;
  message: string | null;
  _count?: { events: number };
}

const levelColor: Record<Level, 'default' | 'warning' | 'error' | 'success'> = {
  info: 'success',
  warn: 'warning',
  error: 'error',
};
const levelLabel: Record<Level, string> = { info: 'OK', warn: 'Advarsel', error: 'Fejl' };
const kindLabel: Record<string, string> = { delta: 'Delta', full: 'Fuld', manual: 'Manuel', history: 'Historik', status: 'Status' };
const runStatusLabel: Record<string, string> = { RUNNING: 'Kører', COMPLETED: 'Færdig', FAILED: 'Fejlet' };

const fmtDate = (v?: string | null) => (v ? format(new Date(v), 'dd.MM.yyyy HH:mm:ss', { locale: da }) : '');
const fmtAgo = (v?: string | null) => (v ? formatDistanceToNow(new Date(v), { locale: da, addSuffix: true }) : '');
const fmtMs = (ms?: number | null) => {
  if (ms === null || ms === undefined) return '';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60000).toFixed(1)} min`;
};
const n = (v: number | null | undefined) => (v === null || v === undefined ? 0 : v);

const summarizeRun = (summary: any): string => {
  if (!summary || typeof summary !== 'object') return '';
  const parts: string[] = [];
  if (summary.customers) parts.push(`Kunder ${summary.customers.created ?? 0}/${summary.customers.updated ?? 0}`);
  if (summary.bookings) parts.push(`Bookinger ${summary.bookings.created ?? 0}/${summary.bookings.updated ?? 0}${summary.bookings.unchanged !== undefined ? `/${summary.bookings.unchanged} uændr.` : ''}`);
  if (summary.offers) parts.push(`Tilbud ${summary.offers.created ?? 0}`);
  if (summary.creditors) parts.push(`Kreditorer fjernet ${summary.creditors.removed ?? 0}`);
  return parts.join(' · ');
};

export const TmsLog: React.FC = () => {
  const [days, setDays] = useState(7);
  const [levels, setLevels] = useState<Level[]>(['error', 'warn']);
  const [endpoint, setEndpoint] = useState('');
  const [search, setSearch] = useState('');
  const [runId, setRunId] = useState<string | null>(null);
  const [eventPage, setEventPage] = useState(1);
  const [runPage, setRunPage] = useState(1);
  const [openEvent, setOpenEvent] = useState<ApiEvent | null>(null);

  const summaryQuery = useQuery({
    queryKey: ['admin', 'tms-log', 'summary', days],
    queryFn: async () => (await adminApi.getTmsLogSummary({ days })).data.data,
    refetchInterval: 60000,
  });

  const runsQuery = useQuery({
    queryKey: ['admin', 'tms-log', 'runs', runPage],
    queryFn: async () => (await adminApi.getTmsLogRuns({ page: runPage, limit: 15 })).data,
    refetchInterval: 60000,
  });

  const eventsQuery = useQuery({
    queryKey: ['admin', 'tms-log', 'events', levels, endpoint, search, runId, eventPage],
    queryFn: async () => (await adminApi.getTmsLogEvents({
      page: eventPage,
      limit: 50,
      level: levels.length > 0 ? levels.join(',') : undefined,
      endpoint: endpoint || undefined,
      search: search || undefined,
      runId: runId || undefined,
    })).data,
    refetchInterval: 60000,
  });

  const summary = summaryQuery.data;
  const endpoints: EndpointRow[] = summary?.byEndpoint ?? [];
  const byHour: { hour: number; errors: number; warnings: number }[] = summary?.byHour ?? [];
  const byDay: { day: string; errors: number; warnings: number; ok: number }[] = summary?.byDay ?? [];
  const maxHourErrors = useMemo(() => Math.max(1, ...byHour.map(h => n(h.errors))), [byHour]);

  const events: ApiEvent[] = eventsQuery.data?.data ?? [];
  const eventPagination = eventsQuery.data?.pagination;
  const runs: SyncRun[] = runsQuery.data?.data ?? [];
  const runPagination = runsQuery.data?.pagination;

  const totalErrors = endpoints.reduce((a, e) => a + n(e.errors), 0);
  const totalWarnings = endpoints.reduce((a, e) => a + n(e.warnings), 0);
  const failingNow = endpoints.filter(e => n(e.errors24h) > 0);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} mb={2} spacing={1}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Flowtale-log</Typography>
          <Typography variant="body2" color="text.secondary">
            Hvornår og på hvilke kald Flowtale-integrationen fejler. Hentes fra databasen, opdateres hvert minut.
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
          <ToggleButton value={1}>24 timer</ToggleButton>
          <ToggleButton value={7}>7 dage</ToggleButton>
          <ToggleButton value={30}>30 dage</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {summaryQuery.isError && <Alert severity="error" sx={{ mb: 2 }}>Kunne ikke hente loggen.</Alert>}

      <Grid container spacing={2} mb={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}><CardContent>
            <Typography variant="caption" color="text.secondary">Fejl i perioden</Typography>
            <Typography variant="h4" fontWeight={700} color={totalErrors > 0 ? 'error.main' : 'text.primary'}>{totalErrors}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}><CardContent>
            <Typography variant="caption" color="text.secondary">Advarsler (retries)</Typography>
            <Typography variant="h4" fontWeight={700} color={totalWarnings > 0 ? 'warning.main' : 'text.primary'}>{totalWarnings}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}><CardContent>
            <Typography variant="caption" color="text.secondary">Endpoints med fejl seneste 24 t</Typography>
            <Typography variant="h4" fontWeight={700} color={failingNow.length > 0 ? 'error.main' : 'success.main'}>{failingNow.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}><CardContent>
            <Typography variant="caption" color="text.secondary">Seneste kørsel</Typography>
            <Typography variant="h6" fontWeight={600}>{runs[0] ? fmtAgo(runs[0].startedAt) : '–'}</Typography>
            <Typography variant="caption" color="text.secondary">{runs[0] ? `${kindLabel[runs[0].kind] ?? runs[0].kind} · ${runStatusLabel[runs[0].status] ?? runs[0].status}` : ''}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1}>Pr. endpoint</Typography>
              {summaryQuery.isLoading ? <CircularProgress size={24} /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Endpoint</TableCell>
                        <TableCell align="right">Fejl</TableCell>
                        <TableCell align="right">24 t</TableCell>
                        <TableCell align="right">Retries</TableCell>
                        <TableCell align="right">OK</TableCell>
                        <TableCell>Seneste fejl</TableCell>
                        <TableCell>Seneste OK</TableCell>
                        <TableCell align="right">Snit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {endpoints.map(e => (
                        <TableRow key={e.endpoint} hover sx={{ cursor: 'pointer' }} onClick={() => { setEndpoint(e.endpoint); setEventPage(1); }}>
                          <TableCell><Typography variant="body2" fontWeight={500}>{e.endpoint}</Typography></TableCell>
                          <TableCell align="right">{n(e.errors) > 0 ? <Chip size="small" color="error" label={n(e.errors)} sx={{ borderRadius: 1 }} /> : 0}</TableCell>
                          <TableCell align="right">{n(e.errors24h)}</TableCell>
                          <TableCell align="right">{n(e.warnings)}</TableCell>
                          <TableCell align="right">{n(e.ok)}</TableCell>
                          <TableCell><Tooltip title={fmtDate(e.lastErrorAt)}><span>{fmtAgo(e.lastErrorAt)}</span></Tooltip></TableCell>
                          <TableCell><Tooltip title={fmtDate(e.lastOkAt)}><span>{fmtAgo(e.lastOkAt)}</span></Tooltip></TableCell>
                          <TableCell align="right">{fmtMs(e.avgDurationMs)}</TableCell>
                        </TableRow>
                      ))}
                      {endpoints.length === 0 && (
                        <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary" py={2}>Ingen kald registreret i perioden</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1}>Fejl pr. time (dansk tid)</Typography>
              <Box display="flex" alignItems="flex-end" gap={0.5} height={120}>
                {Array.from({ length: 24 }, (_, h) => {
                  const row = byHour.find(x => x.hour === h);
                  const errs = n(row?.errors);
                  const warns = n(row?.warnings);
                  return (
                    <Tooltip key={h} title={`${String(h).padStart(2, '0')}:00 · ${errs} fejl · ${warns} retries`}>
                      <Box flex={1} display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                        <Box sx={{ bgcolor: errs > 0 ? 'error.main' : 'action.hover', height: `${Math.max(4, (errs / maxHourErrors) * 100)}%`, borderRadius: 0.5 }} />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
              <Box display="flex" justifyContent="space-between" mt={0.5}>
                <Typography variant="caption" color="text.secondary">00</Typography>
                <Typography variant="caption" color="text.secondary">12</Typography>
                <Typography variant="caption" color="text.secondary">23</Typography>
              </Box>
              <Typography variant="h6" fontWeight={600} mt={2} mb={1}>Pr. dag</Typography>
              <Stack spacing={0.25}>
                {byDay.slice(-14).map(d => (
                  <Box key={String(d.day)} display="flex" justifyContent="space-between">
                    <Typography variant="body2">{format(new Date(d.day), 'EEE dd.MM', { locale: da })}</Typography>
                    <Typography variant="body2" color={n(d.errors) > 0 ? 'error.main' : 'text.secondary'}>
                      {n(d.errors)} fejl · {n(d.warnings)} retries · {n(d.ok)} ok
                    </Typography>
                  </Box>
                ))}
                {byDay.length === 0 && <Typography variant="body2" color="text.secondary">Ingen data</Typography>}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={1}>Kørsler</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Start</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Varighed</TableCell>
                  <TableCell align="right">Fejl</TableCell>
                  <TableCell>Resultat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map(r => (
                  <TableRow key={r.id} hover selected={runId === r.id} sx={{ cursor: 'pointer' }} onClick={() => { setRunId(runId === r.id ? null : r.id); setEventPage(1); }}>
                    <TableCell>{fmtDate(r.startedAt)}</TableCell>
                    <TableCell>{kindLabel[r.kind] ?? r.kind}</TableCell>
                    <TableCell>
                      <Chip size="small" sx={{ borderRadius: 1 }} label={runStatusLabel[r.status] ?? r.status}
                        color={r.status === 'FAILED' ? 'error' : r.status === 'RUNNING' ? 'info' : r.errorCount > 0 ? 'warning' : 'success'} />
                    </TableCell>
                    <TableCell align="right">{fmtMs(r.durationMs)}</TableCell>
                    <TableCell align="right">{r.errorCount}</TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{r.message || summarizeRun(r.summary)}</Typography></TableCell>
                  </TableRow>
                ))}
                {runs.length === 0 && !runsQuery.isLoading && (
                  <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" py={2}>Ingen kørsler endnu</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {runPagination && runPagination.totalPages > 1 && (
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Pagination size="small" count={runPagination.totalPages} page={runPage} onChange={(_, p) => setRunPage(p)} />
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} mb={1}>
            <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
              Hændelser {runId ? '(valgt kørsel)' : ''}
            </Typography>
            <ToggleButtonGroup size="small" value={levels} onChange={(_, v: Level[]) => { setLevels(v); setEventPage(1); }}>
              <ToggleButton value="error">Fejl</ToggleButton>
              <ToggleButton value="warn">Advarsler</ToggleButton>
              <ToggleButton value="info">OK</ToggleButton>
            </ToggleButtonGroup>
            <TextField select size="small" label="Endpoint" value={endpoint} onChange={e => { setEndpoint(e.target.value); setEventPage(1); }} sx={{ minWidth: 200 }}>
              <MenuItem value="">Alle</MenuItem>
              {endpoints.map(e => <MenuItem key={e.endpoint} value={e.endpoint}>{e.endpoint}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Søg i besked" value={search} onChange={e => { setSearch(e.target.value); setEventPage(1); }} sx={{ minWidth: 200 }} />
            {runId && <Chip label="Ryd kørsel-filter" onDelete={() => setRunId(null)} size="small" />}
          </Stack>
          {eventsQuery.isLoading ? <CircularProgress size={24} /> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tidspunkt</TableCell>
                    <TableCell>Niveau</TableCell>
                    <TableCell>Endpoint</TableCell>
                    <TableCell align="right">Position</TableCell>
                    <TableCell align="right">HTTP</TableCell>
                    <TableCell align="right">Rækker</TableCell>
                    <TableCell align="right">Varighed</TableCell>
                    <TableCell>Besked</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map(ev => (
                    <TableRow key={ev.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenEvent(ev)}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(ev.createdAt)}</TableCell>
                      <TableCell><Chip size="small" sx={{ borderRadius: 1 }} color={levelColor[ev.level]} label={levelLabel[ev.level]} /></TableCell>
                      <TableCell>{ev.endpoint}</TableCell>
                      <TableCell align="right">{ev.position ?? ''}</TableCell>
                      <TableCell align="right">{ev.httpStatus ?? ''}</TableCell>
                      <TableCell align="right">{ev.itemsFetched ?? ''}</TableCell>
                      <TableCell align="right">{fmtMs(ev.durationMs)}</TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body2" noWrap title={ev.message ?? ''}>{ev.message}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {events.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary" py={2}>Ingen hændelser matcher filtret</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {eventPagination && eventPagination.totalPages > 1 && (
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Pagination size="small" count={eventPagination.totalPages} page={eventPage} onChange={(_, p) => setEventPage(p)} />
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!openEvent} onClose={() => setOpenEvent(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Hændelse
          <IconButton size="small" onClick={() => setOpenEvent(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          {openEvent && (
            <Stack spacing={1}>
              <Typography variant="body2"><b>Tidspunkt:</b> {fmtDate(openEvent.createdAt)}</Typography>
              <Typography variant="body2"><b>Endpoint:</b> {openEvent.endpoint} {openEvent.region ? `(${openEvent.region})` : ''}</Typography>
              <Typography variant="body2"><b>Niveau:</b> {levelLabel[openEvent.level]}</Typography>
              {openEvent.httpStatus !== null && <Typography variant="body2"><b>HTTP-status:</b> {openEvent.httpStatus}</Typography>}
              {openEvent.position !== null && <Typography variant="body2"><b>Position (skip/offset):</b> {openEvent.position}</Typography>}
              {openEvent.itemsFetched !== null && <Typography variant="body2"><b>Rækker hentet:</b> {openEvent.itemsFetched}</Typography>}
              {openEvent.durationMs !== null && <Typography variant="body2"><b>Varighed:</b> {fmtMs(openEvent.durationMs)}</Typography>}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}><b>Besked:</b> {openEvent.message}</Typography>
              {openEvent.params && (
                <Box component="pre" sx={{ fontSize: 12, bgcolor: 'action.hover', p: 1, borderRadius: 1, overflowX: 'auto' }}>
                  {JSON.stringify(openEvent.params, null, 2)}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TmsLog;
